import {skipToken} from '@reduxjs/toolkit/query';
import {Checkbox, TableRow} from 'platform/components';
import {Box, Center, Hide, HStack, Icon, Right, Show, Spinner, Text} from 'platform/foundation';
import {NumberFormat, useFormatNumber, useLocale} from 'platform/locale';

import {RefObject, useEffect, useRef, useState} from 'react';

import {head, isEmpty, isNotEmpty, isNotNil, once, tail, times} from 'ramda';
import {round} from 'ramda-adjunct';

import {useDrillDownQuery} from '../../../../../store/omneticApi';
import {
  DrillDownLevel,
  DrillDownLevelName,
  DrillDownResponse,
  DrillDownStatistics,
  DrillDownStatisticsLevel,
} from '../../../../../types/api/drillDown';
import {useDrillDownWithGlobalRowsMarketCounts} from '../hooks/useDrillDownWithGlobalRowsMarketCounts';
import {LevelConfiguration} from '../types';
import {getFirstColumnText} from '../utils/getFirstColumnText';
import {getGapColor} from '../utils/getGapColor';
import {getKeyFromLevels} from '../utils/getKeyFromLevels';
import {isRowSelected} from '../utils/isRowSelected';

export interface DrillDownRowProps {
  depth: number;
  levelConfigurations: LevelConfiguration[];
  data: DrillDownStatistics;
  translations: DrillDownResponse['translations'];
  currency: string;
  regions: string[];
  selectedRow?: Record<DrillDownLevelName, string | undefined>;
  scrollToSelectedRow?: boolean;
  boldFontForSelectedRowOnly?: boolean;
  isSimplified?: boolean;
  hasGlobalRowsWithMarketCounts?: boolean;
  minYear?: number;
  onSelect?: (levels: DrillDownStatisticsLevel[]) => void;
}

const scrollOnSelectedRowOnce = once((ref: RefObject<HTMLDivElement | null>) =>
  ref.current?.scrollIntoView({behavior: 'smooth'})
);

export function DrillDownRow(props: DrillDownRowProps) {
  const {language} = useLocale();
  const formatNumber = useFormatNumber();

  const isSelectedRow = isRowSelected(props.data, props.selectedRow);
  const [isExpanded, setIsExpanded] = useState(isSelectedRow);
  useEffect(() => {
    setIsExpanded(isSelectedRow);
  }, [isSelectedRow, setIsExpanded]);

  const queryLevels: DrillDownLevel[] = props.data.levels.map((level, index) => ({
    level: level.name,
    values: [level.value],
    priority: index + 1,
  }));

  const currentLevelConfiguration = head(props.levelConfigurations);
  const nextLevelConfigurations = tail(props.levelConfigurations);
  const isLastLevel = isEmpty(nextLevelConfigurations);

  const queryArg = isNotEmpty(nextLevelConfigurations)
    ? {
        levels: [
          ...queryLevels,
          {
            level: head(nextLevelConfigurations).name,
            values: [],
            priority: queryLevels.length + 1,
          },
        ],
        currency: props.currency,
        lang: language,
      }
    : undefined;

  const singleRegionResult = useDrillDownQuery(
    isNotNil(queryArg) ? {...queryArg, regions: props.regions} : skipToken,
    {skip: !isExpanded || props.hasGlobalRowsWithMarketCounts}
  );

  const globalRowsMarketCountsResult = useDrillDownWithGlobalRowsMarketCounts(
    isNotNil(queryArg)
      ? {
          ...queryArg,
          regions: props.regions,
          minYear: props.minYear,
        }
      : skipToken,
    {skip: !isExpanded || !props.hasGlobalRowsWithMarketCounts}
  );

  const result = props.hasGlobalRowsWithMarketCounts
    ? globalRowsMarketCountsResult
    : singleRegionResult;
  const {data, isLoading} = result;

  const sold = props.data.statistics.find((item) => item.type === 'sold');
  const stock = props.data.statistics.find((item) => item.type === 'stock');

  const gap = (sold?.amount ?? 0) - (stock?.amount ?? 0);

  const soldMileage = round((sold?.mileage ?? 0) / 1000);
  const stockMileage = round((stock?.mileage ?? 0) / 1000);
  const soldPrice = round((sold?.price ?? 0) / 1000);
  const stockPrice = round((stock?.price ?? 0) / 1000);

  const isTargetRow = isLastLevel && isRowSelected(props.data, props.selectedRow);
  const rowHeight = props.isSimplified ? 7 : 6;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.scrollToSelectedRow && isTargetRow) {
      scrollOnSelectedRowOnce(ref);
    }
  }, [props.scrollToSelectedRow, isTargetRow]);

  const additionalColumns = props.isSimplified
    ? [
        <Box paddingHorizontal={1} paddingRight={4} key="totalAmount">
          <Right>
            <Text size="xSmall">
              <NumberFormat number={(sold?.amount ?? 0) + (stock?.amount ?? 0)} />
            </Text>
          </Right>
        </Box>,
      ]
    : [
        <Box paddingHorizontal={1} key="soldAmount">
          <Right>
            <Text size="xSmall">
              <NumberFormat number={sold?.amount} />
            </Text>
          </Right>
        </Box>,
        <Box paddingHorizontal={1} key="stockAmount">
          <Right>
            <Text size="xSmall">
              <NumberFormat number={stock?.amount} />
            </Text>
          </Right>
        </Box>,
        <Box paddingHorizontal={1} key="soldPrice">
          <Right>
            <Text size="xSmall">{soldPrice === 0 ? '0' : `${formatNumber(soldPrice, 0)}k`}</Text>
          </Right>
        </Box>,
        <Box paddingHorizontal={1} key="stockPrice">
          <Right>
            <Text size="xSmall">{stockPrice === 0 ? '0' : `${formatNumber(stockPrice, 0)}k`}</Text>
          </Right>
        </Box>,
        <Box paddingHorizontal={1} key="soldMileage">
          <Right>
            <Text size="xSmall">
              {soldMileage === 0 ? '0' : `${formatNumber(soldMileage, 0)}k`}
            </Text>
          </Right>
        </Box>,
        <Box paddingHorizontal={1} key="stockMileage">
          <Right>
            <Text size="xSmall">
              {stockMileage === 0 ? '0' : `${formatNumber(stockMileage, 0)}k`}
            </Text>
          </Right>
        </Box>,
        <Box paddingHorizontal={1} paddingRight={4} key="gap">
          <Right>
            <Text size="xSmall" color={getGapColor(gap)}>
              <NumberFormat number={gap} decimals={0} />
            </Text>
          </Right>
        </Box>,
      ];

  return (
    <>
      <TableRow
        color={isTargetRow ? 'palettes.blue.10.100' : undefined}
        hoverColor={isTargetRow ? 'palettes.blue.20.100' : undefined}
        onClick={
          !isLastLevel
            ? () => setIsExpanded(!isExpanded)
            : () => props.onSelect?.(props.data.levels)
        }
      >
        <HStack key="label" align="center" spacing={2} height={rowHeight}>
          <Box ref={ref} />
          <Show when={props.depth > 0}>
            {times(
              (index) => (
                <Box key={index} paddingLeft={2}>
                  <Box
                    height={rowHeight}
                    borderRight={
                      isLastLevel && isNotNil(props.onSelect) && index === props.depth - 1
                        ? 'none'
                        : '1px solid'
                    }
                    borderColor="palettes.neutral.40.100"
                  />
                </Box>
              ),
              props.depth
            )}
          </Show>
          <Hide when={isLastLevel}>
            <Center height={rowHeight} width={4}>
              {isLoading ? (
                <Spinner size="small" />
              ) : (
                <Icon
                  value={isExpanded ? 'navigation/arrow_drop_down' : 'navigation/arrow_right'}
                  size={6}
                  color={isExpanded ? 'palettes.blue.60.100' : 'palettes.neutral.50.100'}
                />
              )}
            </Center>
          </Hide>
          <Show when={isLastLevel && isNotNil(props.onSelect)}>
            <Checkbox value={isSelectedRow} />
          </Show>
          <Text
            size="xSmall"
            alternative={!props.boldFontForSelectedRowOnly || isSelectedRow}
            noWrap
          >
            {getFirstColumnText(props.data, currentLevelConfiguration, props.translations)}
          </Text>
        </HStack>
        {additionalColumns}
      </TableRow>
      <Show when={isExpanded}>
        {data?.statisticsAggregated.map((item) => (
          <DrillDownRow
            key={getKeyFromLevels(item.levels)}
            depth={props.depth + 1}
            levelConfigurations={nextLevelConfigurations}
            data={item}
            translations={data?.translations}
            currency={props.currency}
            regions={props.regions}
            selectedRow={props.selectedRow}
            scrollToSelectedRow={props.scrollToSelectedRow}
            boldFontForSelectedRowOnly={props.boldFontForSelectedRowOnly}
            isSimplified={props.isSimplified}
            hasGlobalRowsWithMarketCounts={props.hasGlobalRowsWithMarketCounts}
            minYear={props.minYear}
            onSelect={props.onSelect}
          />
        ))}
      </Show>
    </>
  );
}
